import React from 'react';
import { Box } from '@mui/material';
import { anchorRef, bindTrigger, usePopupState, bindPopover } from 'material-ui-popup-state/hooks';

import { TableHeader, TableContainer, FilterButton } from '@occc-app/design-system';
import { Tab, Tabs } from '@occc/ui-design-system';
import { useTabQueryParam } from '@occc/utils-query-params';

import Audiences from './Audiences';
import Segments from './Segments';
import { TargetingTab } from './types';
import { TARGETING_TAB_PARAM, TARGETING_TABS, DEFAULT_TARGETING_TAB } from './constants';

type TargetingProps = {
  memberId: string;
};

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';
const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const targetingCommonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const audiencesTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const segmentsTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});

export const Targeting: React.FC<TargetingProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useTabQueryParam<TargetingTab>(
    TARGETING_TAB_PARAM,
    DEFAULT_TARGETING_TAB,
    TARGETING_TABS,
  );
  const audiencesPopupState = usePopupState({ variant: 'popover', popupId: 'audiencesFilters' });
  const audiencesPopoverProps = {
    ...bindPopover({
      ...audiencesPopupState,
      isOpen: audiencesPopupState.isOpen && activeTab === 'audiences',
    }),
  };

  const handleTabChange = (_event: React.SyntheticEvent<Element, Event>, value: TargetingTab) => {
    setActiveTab(value);
  };

  const renderAudiencesActions = () => (
    <FilterButton
      {...bindTrigger(audiencesPopupState)}
      ref={anchorRef(audiencesPopupState)}
      showBadge
    />
  );

  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      <TableHeader
        disableTitleTypography
        tabs={
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab value={'audiences' satisfies TargetingTab} label='Audiences' />
            <Tab value={'segments' satisfies TargetingTab} label='Segments' />
          </Tabs>
        }
        actions={activeTab === 'audiences' ? renderAudiencesActions() : undefined}
      />
      <Box style={targetingBoxStyles}>
        <Box
          id={'audiences' satisfies TargetingTab}
          style={audiencesTabStyles(activeTab === 'audiences')}
        >
          <Audiences memberId={memberId} popoverProps={audiencesPopoverProps} />
        </Box>
        <Box
          id={'segments' satisfies TargetingTab}
          style={segmentsTabStyles(activeTab === 'segments')}
        >
          <Segments memberId={memberId} />
        </Box>
      </Box>
    </TableContainer>
  );
};
